// ---------------------------------------------------------------------------
// mapboxTraffic — fetch a traffic-adjusted ETA for a given route polyline so
// we can demote routes whose "fastest" label evaporates in real-world
// congestion. Without this, a route through a chronically busy arterial keeps
// showing as the fastest pick at 5pm even though Google would route around it.
//
// Approach: call the Mapbox Directions API with the `driving-traffic` profile
// and a small set of waypoints sampled from the candidate route's polyline.
// Mapbox returns the live-traffic duration along that path — we compare it to
// the engine's optimistic duration to derive a "traffic delta" in seconds.
//
// The Mapbox public access token is safe to ship to the client (it's designed
// for browser use; you restrict it to your app's URL/bundle id in the dashboard).
// ---------------------------------------------------------------------------

import { MAPBOX_TOKEN } from "@/lib/env";
import type { LngLat } from "@/utils/polyline";

/** Categorical Mapbox congestion levels. "unknown" maps to no signal. */
export type CongestionLevel = "low" | "moderate" | "heavy" | "severe" | "unknown";

/** Per-leg traffic signal — one entry for each sampled segment between
 *  consecutive waypoints in the request. Lets scoring/UI describe WHERE
 *  the congestion is, not just whether it exists in aggregate (#66). */
export interface TrafficLeg {
  /** Coordinates bounding this leg (start, end). Useful for plotting or
   *  matching back to the original polyline. */
  startCoord: LngLat;
  endCoord: LngLat;
  /** Live-traffic duration in seconds along this leg. */
  durationS: number;
  /** Typical free-flow duration for this leg if Mapbox returned it. When
   *  null, fall back to `null` — the consumer can use the route-level
   *  freeFlow ratio as a backup. */
  freeFlowS: number | null;
  /** Worst congestion level along this leg (from Mapbox's per-segment
   *  congestion annotation, max'd across the leg's sub-segments). */
  congestion: CongestionLevel;
  /** Leg distance in meters. */
  distanceM: number;
}

export interface TrafficEta {
  /** Live-traffic-adjusted travel time in seconds along the requested path. */
  durationS: number;
  /** Distance Mapbox snapped along, in meters. Useful sanity-check vs.
   *  the engine's distance — large divergence means we sampled poorly. */
  distanceM: number;
  /** Free-flow (no-traffic) baseline if Mapbox returned it, otherwise null.
   *  Some response shapes include `duration_typical_traffic`; we surface it
   *  when present so callers can compute the delta without a second call. */
  freeFlowS: number | null;
  /** Per-leg traffic signal. Empty array when Mapbox didn't return leg
   *  data — callers should treat that as "use whole-route delta instead." */
  legs: TrafficLeg[];
}

const MAX_WAYPOINTS = 25; // Mapbox Directions hard limit per request.
const DEFAULT_SAMPLE = 10; // Reasonable for most trips; keeps URL short.

// In-memory dedupe so simultaneous renders (e.g. several RouteCards mounting
// in the same tick) collapse into a single network call per route polyline.
const inflight = new Map<string, Promise<TrafficEta | null>>();

/** Pick N evenly-spaced waypoints from a polyline, always including start +
 *  end. Returns at most MAX_WAYPOINTS coordinates. */
const sampleWaypoints = (coords: LngLat[], n = DEFAULT_SAMPLE): LngLat[] => {
  if (coords.length <= n) return coords;
  const out: LngLat[] = [];
  const step = (coords.length - 1) / (n - 1);
  for (let i = 0; i < n; i++) {
    const idx = Math.min(coords.length - 1, Math.round(i * step));
    out.push(coords[idx]);
  }
  // Final dedupe in case rounding produced repeats at the end.
  return out.filter((c, i, arr) => i === 0 || c[0] !== arr[i - 1][0] || c[1] !== arr[i - 1][1])
    .slice(0, MAX_WAYPOINTS);
};

const cacheKey = (coords: LngLat[]): string => {
  // Start + end + length ALONE collide for two different routes on the same
  // trip: Scenic and Pleasant share the start/end, and capRouteGeometry caps
  // long routes to the same point count — so the in-flight dedupe served ONE
  // route's ETA to every card (bug: a 1233mi and a 1091mi route both showing
  // 17h29m). Sample interior points too, where the geometries actually differ,
  // so distinct routes get distinct keys.
  const n = coords.length;
  const at = (i: number): string => {
    const c = coords[Math.max(0, Math.min(n - 1, i))];
    return c ? `${c[0].toFixed(3)},${c[1].toFixed(3)}` : "";
  };
  return (
    `${n}@${at(0)}|${at(Math.floor(n * 0.25))}|${at(Math.floor(n * 0.5))}` +
    `|${at(Math.floor(n * 0.75))}|${at(n - 1)}`
  );
};

/** Reduce a Mapbox congestion-annotation array to its worst level. */
const CONGESTION_RANK: Record<CongestionLevel, number> = {
  unknown: 0,
  low: 1,
  moderate: 2,
  heavy: 3,
  severe: 4,
};
const worstCongestion = (arr: string[]): CongestionLevel => {
  let best: CongestionLevel = "unknown";
  for (const v of arr) {
    const lv = (v as CongestionLevel);
    if (
      lv === "low" || lv === "moderate" || lv === "heavy" ||
      lv === "severe" || lv === "unknown"
    ) {
      if (CONGESTION_RANK[lv] > CONGESTION_RANK[best]) best = lv;
    }
  }
  return best;
};

/** Fetch a live-traffic ETA along the given polyline. Returns null on any
 *  error (network, quota, malformed response) — callers should treat null as
 *  "no traffic signal available," not as "no traffic." */
export const fetchTrafficEta = async (coordinates: LngLat[]): Promise<TrafficEta | null> => {
  if (!MAPBOX_TOKEN || coordinates.length < 2) return null;
  const key = cacheKey(coordinates);
  const existing = inflight.get(key);
  if (existing) return existing;

  const waypoints = sampleWaypoints(coordinates);
  const coordStr = waypoints.map((c) => `${c[0]},${c[1]}`).join(";");
  // Annotations:
  //   duration         — per-sub-segment time (gives us leg durations too)
  //   speed            — diagnostic for "is this leg crawling?"
  //   congestion       — Mapbox's categorical low/moderate/heavy/severe
  //                      per sub-segment. The data we actually want for
  //                      segment-level penalty (#66).
  // Mapbox REQUIRES `overview=full` whenever `congestion` is requested —
  // otherwise it rejects the call ("Overview option must be full for
  // congestion"). This was previously `overview=false`, so EVERY live-traffic
  // call failed and traffic-adjusted ETAs / the "+X min traffic" data never
  // worked. (Morgan spotted the error in the console.)
  const url =
    `https://api.mapbox.com/directions/v5/mapbox/driving-traffic/${coordStr}` +
    `?access_token=${encodeURIComponent(MAPBOX_TOKEN)}` +
    `&overview=full&annotations=duration,speed,congestion`;

  const p = (async (): Promise<TrafficEta | null> => {
    // Hard timeout: on weak cell a bare fetch can hang for 60s+, and those
    // stalled requests pile up and make the whole nav view laggy (Morgan: "very
    // laggy when service got bad"). Abort after 8s and treat it as no signal —
    // navigation keeps running fine on the planned ETA. navigator.onLine only
    // catches FULLY offline, not slow, so the timeout is what actually helps.
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(), 8_000);
    try {
      const res = await fetch(url, { signal: ctrl.signal });
      if (!res.ok) return null;
      const json = await res.json();
      const route = json?.routes?.[0];
      if (!route) return null;
      // Parse per-leg signals. legs[i] corresponds to the segment between
      // waypoints[i] and waypoints[i+1]. Mapbox attaches an `annotation`
      // object per leg with arrays of per-sub-segment values.
      const legs: TrafficLeg[] = [];
      const apiLegs = Array.isArray(route.legs) ? route.legs : [];
      for (let i = 0; i < apiLegs.length; i++) {
        const leg = apiLegs[i];
        const start = waypoints[i];
        const end = waypoints[i + 1];
        if (!start || !end) continue;
        const congArr: string[] = Array.isArray(leg?.annotation?.congestion)
          ? leg.annotation.congestion
          : [];
        // Worst-case congestion along the leg drives our scoring weight —
        // a single severe stretch in the middle of an otherwise calm leg
        // is more user-relevant than an averaged "moderate."
        const worst = worstCongestion(congArr);
        legs.push({
          startCoord: start,
          endCoord: end,
          durationS: Number(leg.duration) || 0,
          freeFlowS: typeof leg.duration_typical === "number" ? leg.duration_typical : null,
          congestion: worst,
          distanceM: Number(leg.distance) || 0,
        });
      }
      return {
        durationS: Number(route.duration) || 0,
        distanceM: Number(route.distance) || 0,
        // `duration_typical` is the no-traffic baseline; not always present.
        freeFlowS: typeof route.duration_typical === "number" ? route.duration_typical : null,
        legs,
      };
    } catch {
      return null;
    } finally {
      window.clearTimeout(timer);
      // Allow the next call after a short delay so transient network errors
      // can retry quickly, without thrashing the API for a stable result.
      window.setTimeout(() => inflight.delete(key), 30_000);
    }
  })();

  inflight.set(key, p);
  return p;
};
